/**
 * Orchestrates the "Publish to Web" flow:
 * 1. Authenticate + get upload token
 * 2. Gather export data (reuses htmlExporter)
 * 3. Upload media to Vercel Blob (client-side direct upload)
 * 4. Patch export data with blob URLs
 * 5. Build HTML with patched data
 * 6. Deploy via Edge Function
 * 7. Return deployment URL
 */

import i18next from 'i18next';
import { gatherExportData, measureThumbnailBytes } from './htmlExporter.ts';
import { buildPlayerHtml } from './playerTemplate.ts';
import { getBlob } from '@/previs/audioBlobStore.ts';
import { requireAuth } from '@/api/supabaseClient.ts';
import { getUploadToken, deployHtml } from '@/api/publishApi.ts';
import type { ExportAudioClip } from './htmlExporter.ts';
import { useProjectStore } from '@/store/projectStore.ts';
import { useAudioStore } from '@/store/audioStore.ts';
import type { DeployStats } from '@/api/publishApi.ts';
import { dataUrlToBytes } from '@/utils/dataUrlUtils.ts';

// Re-export for consumers that previously imported from here
export { dataUrlToBytes } from '@/utils/dataUrlUtils.ts';

// ── Types ────────────────────────────────────────────────────────────

export interface PublishProgress {
  stage: 'preparing' | 'uploading-images' | 'uploading-audio' | 'uploading-video' | 'building' | 'deploying' | 'done';
  current: number;
  total: number;
  message: string;
}

export interface PublishOptions {
  includeVideo: boolean;
  compressImages: boolean;
  onProgress: (progress: PublishProgress) => void;
}

export interface PublishResult {
  deploymentUrl: string;
  exportId: string;
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Upload a single file to Vercel Blob via client-side PUT */
export async function uploadToBlob(
  clientToken: string,
  pathname: string,
  fileBytes: Uint8Array,
  contentType: string,
): Promise<string> {
  const resp = await fetch(`https://blob.vercel-storage.com/${pathname}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${clientToken}`,
      'x-content-type': contentType,
      'x-api-version': '7',
    },
    body: fileBytes as unknown as BodyInit,
  });

  if (!resp.ok) {
    const errText = await resp.text();
    throw new Error(`Blob upload failed for ${pathname}: ${errText}`);
  }

  const result = await resp.json();
  return result.url as string;
}

/** Run async tasks with limited concurrency */
export async function concurrentMap<T, R>(
  items: T[],
  concurrency: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const i = nextIndex++;
      results[i] = await fn(items[i], i);
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Translate a key from the export namespace (non-component context) */
function te(key: string, options?: Record<string, unknown>): string {
  return i18next.t(key, { ns: 'export', ...options });
}

// ── Main publish function ────────────────────────────────────────────

export async function publishToWeb(options: PublishOptions): Promise<PublishResult> {
  const { includeVideo, compressImages, onProgress } = options;

  // Stage 1: Prepare — authenticate and get upload token
  onProgress({ stage: 'preparing', current: 0, total: 1, message: te('publishProgressAuthenticating') });
  await requireAuth();

  onProgress({ stage: 'preparing', current: 0, total: 1, message: te('publishProgressGettingToken') });
  const { clientToken, exportId, pathPrefix } = await getUploadToken();

  // Stage 2: Gather export data
  onProgress({ stage: 'preparing', current: 1, total: 1, message: te('publishProgressGathering') });
  // includeDoodles deliberately false: published pages are the static player, matching the
  // Download-HTML checkbox's default (no checkbox exists on the publish tab).
  const exportData = await gatherExportData(includeVideo, compressImages, false);

  // Collect all blob URLs for cleanup tracking
  const allBlobUrls: string[] = [];

  // Stage 3: Upload thumbnails to Vercel Blob
  const thumbnails: { sceneIdx: number; shotIdx: number; dataUrl: string }[] = [];
  for (let si = 0; si < exportData.scenes.length; si++) {
    for (let shi = 0; shi < exportData.scenes[si].shots.length; shi++) {
      const shot = exportData.scenes[si].shots[shi];
      if (shot.thumbnailDataUrl) {
        thumbnails.push({ sceneIdx: si, shotIdx: shi, dataUrl: shot.thumbnailDataUrl });
      }
    }
  }

  if (thumbnails.length > 0) {
    onProgress({ stage: 'uploading-images', current: 0, total: thumbnails.length, message: te('publishProgressUploadingThumbnails', { count: thumbnails.length }) });

    await concurrentMap(thumbnails, 5, async (thumb, idx) => {
      const { bytes, mimeType } = dataUrlToBytes(thumb.dataUrl);
      const ext = mimeType === 'image/jpeg' ? 'jpg' : 'png';
      const pathname = `${pathPrefix}thumb_${thumb.sceneIdx}_${thumb.shotIdx}.${ext}`;
      const blobUrl = await uploadToBlob(clientToken, pathname, bytes, mimeType);
      allBlobUrls.push(blobUrl);

      // Replace the data URL with the blob URL
      exportData.scenes[thumb.sceneIdx].shots[thumb.shotIdx].thumbnailDataUrl = blobUrl;

      onProgress({ stage: 'uploading-images', current: idx + 1, total: thumbnails.length, message: te('publishProgressUploadedThumbnail', { current: idx + 1, total: thumbnails.length }) });
    });
  }

  // Stage 4: Upload audio to Vercel Blob
  const audioClips: { clipIdx: number; clip: ExportAudioClip }[] = [];
  for (let i = 0; i < exportData.audioClips.length; i++) {
    const clip = exportData.audioClips[i];
    if (clip.rawBase64) {
      audioClips.push({ clipIdx: i, clip });
    }
  }

  if (audioClips.length > 0) {
    onProgress({ stage: 'uploading-audio', current: 0, total: audioClips.length, message: te('publishProgressUploadingAudio', { count: audioClips.length }) });

    await concurrentMap(audioClips, 3, async (item, idx) => {
      // Use raw bytes from blob store (canonical source)
      let bytes: Uint8Array;
      let mimeType: string;

      const rawBytes = getBlob(item.clip.id);
      if (rawBytes) {
        bytes = rawBytes;
        mimeType = item.clip.mimeType || 'audio/mpeg';
      } else if (item.clip.rawBase64) {
        const parsed = dataUrlToBytes(item.clip.rawBase64);
        bytes = parsed.bytes;
        mimeType = parsed.mimeType;
      } else {
        return;
      }

      const ext = mimeType.includes('wav') ? 'wav' : mimeType.includes('ogg') ? 'ogg' : 'mp3';
      const pathname = `${pathPrefix}audio_${item.clip.id}.${ext}`;
      const blobUrl = await uploadToBlob(clientToken, pathname, bytes, mimeType);
      allBlobUrls.push(blobUrl);

      // Set mediaUrl and clear rawBase64 (no need for inline data anymore)
      exportData.audioClips[item.clipIdx].mediaUrl = blobUrl;
      exportData.audioClips[item.clipIdx].rawBase64 = null;

      onProgress({ stage: 'uploading-audio', current: idx + 1, total: audioClips.length, message: te('publishProgressUploadedAudio', { current: idx + 1, total: audioClips.length }) });
    });
  }

  // Stage 5: Upload video to Vercel Blob
  if (includeVideo) {
    const videoShots: { sceneIdx: number; shotIdx: number; dataUrl: string }[] = [];
    for (let si = 0; si < exportData.scenes.length; si++) {
      for (let shi = 0; shi < exportData.scenes[si].shots.length; shi++) {
        const shot = exportData.scenes[si].shots[shi];
        if (shot.videoDataUrl) {
          videoShots.push({ sceneIdx: si, shotIdx: shi, dataUrl: shot.videoDataUrl });
        }
      }
    }

    if (videoShots.length > 0) {
      onProgress({ stage: 'uploading-video', current: 0, total: videoShots.length, message: te('publishProgressUploadingVideo', { count: videoShots.length }) });

      await concurrentMap(videoShots, 2, async (vid, idx) => {
        const { bytes, mimeType } = dataUrlToBytes(vid.dataUrl);
        const ext = mimeType.includes('webm') ? 'webm' : 'mp4';
        const pathname = `${pathPrefix}video_${vid.sceneIdx}_${vid.shotIdx}.${ext}`;
        const blobUrl = await uploadToBlob(clientToken, pathname, bytes, mimeType);
        allBlobUrls.push(blobUrl);

        // Replace the data URL with the blob URL
        exportData.scenes[vid.sceneIdx].shots[vid.shotIdx].videoDataUrl = blobUrl;

        onProgress({ stage: 'uploading-video', current: idx + 1, total: videoShots.length, message: te('publishProgressUploadedVideo', { current: idx + 1, total: videoShots.length }) });
      });
    }
  }

  // Stage 5b: Upload continuous transition videos to Vercel Blob
  const transitionVideos: { idx: number; url: string }[] = [];
  for (let i = 0; i < exportData.transitions.length; i++) {
    const t = exportData.transitions[i];
    if (t.videoObjectUrl) {
      transitionVideos.push({ idx: i, url: t.videoObjectUrl });
    }
  }

  if (transitionVideos.length > 0) {
    onProgress({ stage: 'uploading-video', current: 0, total: transitionVideos.length, message: te('publishProgressUploadingTransitions', { count: transitionVideos.length }) });

    await concurrentMap(transitionVideos, 2, async (tv, idx) => {
      const resp = await fetch(tv.url);
      const blob = await resp.blob();
      const bytes = new Uint8Array(await blob.arrayBuffer());
      const mimeType = blob.type || 'video/mp4';
      const ext = mimeType.includes('webm') ? 'webm' : 'mp4';
      const pathname = `${pathPrefix}trans_video_${idx}.${ext}`;
      const blobUrl = await uploadToBlob(clientToken, pathname, bytes, mimeType);
      allBlobUrls.push(blobUrl);

      exportData.transitions[tv.idx].videoObjectUrl = blobUrl;

      onProgress({ stage: 'uploading-video', current: idx + 1, total: transitionVideos.length, message: te('publishProgressUploadedTransition', { current: idx + 1, total: transitionVideos.length }) });
    });
  }

  // Stage 6: Build HTML with patched data (blob URLs instead of data URIs)
  onProgress({ stage: 'building', current: 0, total: 1, message: te('publishProgressBuilding') });
  const html = buildPlayerHtml(exportData);

  // Stage 7: Deploy via Edge Function
  onProgress({ stage: 'deploying', current: 0, total: 1, message: te('publishProgressDeploying') });

  const stats: DeployStats = {
    totalBytes: new TextEncoder().encode(html).length,
    shotCount: exportData.scenes.reduce((n, s) => n + s.shots.length, 0),
    sceneCount: exportData.scenes.length,
    durationSeconds: exportData.totalDuration,
  };

  const result = await deployHtml(html, exportData.title, allBlobUrls, exportId, stats);

  onProgress({ stage: 'done', current: 1, total: 1, message: te('publishProgressDone') });

  return {
    deploymentUrl: result.deployment_url,
    exportId: result.export_id,
  };
}

// ── Size estimation for publish (no base64 overhead) ────────────────

export async function estimatePublishSize(
  includeVideo: boolean,
  compressImages: boolean = true,
): Promise<{ thumbnailsMB: number; audioMB: number; videoMB: number; totalMB: number }> {
  const projectState = useProjectStore.getState();
  const { project } = projectState;
  const { audioClips } = useAudioStore.getState();
  if (!project) return { thumbnailsMB: 0, audioMB: 0, videoMB: 0, totalMB: 0 };

  const activeScenes = projectState.getActiveScenes();

  // Thumbnails: raw bytes (no base64 overhead for blob uploads)
  let thumbBytes = 0;
  for (const scene of activeScenes) {
    for (const shot of scene.shots) {
      if (!shot.thumbnailDataUrl) continue;
      // measureThumbnailBytes returns base64-inflated bytes; publish uploads RAW, so undo it.
      thumbBytes += (await measureThumbnailBytes(shot.thumbnailDataUrl)) / 1.37;
    }
  }
  if (compressImages) thumbBytes *= 0.3;

  // Audio: raw bytes from blob store
  let audioBytes = 0;
  for (const clip of audioClips) {
    const raw = getBlob(clip.id);
    if (raw) {
      audioBytes += raw.byteLength; // no base64 overhead
    }
  }

  // Video: blob sizes. Walks the SHOTS, not projectStore.videoShots — that array is derived from
  // generation-history rows and so holds no animatics, which made an animatic-only project report
  // 0 MB. Same oracle as gatherExportData (shot.videoObjectUrl) or the estimate lies.
  let videoBytes = 0;
  if (includeVideo) {
    for (const scene of activeScenes) {
      for (const shot of scene.shots) {
        if (!shot.videoObjectUrl) continue;
        try {
          const resp = await fetch(shot.videoObjectUrl);
          const blob = await resp.blob();
          videoBytes += blob.size; // no base64 overhead
        } catch { /* skip */ }
      }
    }
  }

  const toMB = (b: number) => b / (1024 * 1024);
  const thumbnailsMB = toMB(thumbBytes);
  const audioMB = toMB(audioBytes);
  const videoMB = toMB(videoBytes);
  const totalMB = thumbnailsMB + audioMB + (includeVideo ? videoMB : 0) + 0.02; // ~20KB for HTML/CSS/JS

  return { thumbnailsMB, audioMB, videoMB, totalMB };
}
